import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Paper,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import { ConsultationGuideData } from "../GuideDetailsDialog";
import { useValueSet } from "@/hooks/useValueSet";

interface ExecutingProfessionalSectionProps {
  formData: ConsultationGuideData;
  onFieldChange: (name: string, value: any) => void;
}

// Opções de conselho profissional
const councilOptions = [
  {
    display: "CRM - Conselho Regional de Medicina",
    value: "06", // Código TISS
    system: "CRM",
  },
  {
    display: "COREN - Conselho Regional de Enfermagem",
    value: "02", // Código TISS
    system: "COREN",
  },
  {
    display: "CRO - Conselho Regional de Odontologia",
    value: "08", // Código TISS
    system: "CRO",
  },
  {
    display:
      "CREFITO - Conselho Regional de Fisioterapia e Terapia Ocupacional",
    value: "05", // Código TISS
    system: "CREFITO",
  },
  {
    display: "CRP - Conselho Regional de Psicologia",
    value: "09", // Código TISS
    system: "CRP",
  },
  {
    display: "CRF - Conselho Regional de Farmácia",
    value: "03", // Código TISS
    system: "CRF",
  },
  {
    display: "CRN - Conselho Regional de Nutricionistas",
    value: "07", // Código TISS
    system: "CRN",
  },
  {
    display: "CRBM - Conselho Regional de Biomedicina",
    value: "10", // Código TISS
    system: "CRBM",
  },
  {
    display: "CREFONO - Conselho Regional de Fonoaudiologia",
    value: "04", // Código TISS
    system: "CREFONO",
  },
  {
    display: "CREF - Conselho Regional de Educação Física",
    value: "11", // Código TISS
    system: "CREF",
  },
];

export const ExecutingProfessionalSection = ({
  formData,
  onFieldChange,
}: ExecutingProfessionalSectionProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    onFieldChange(name, value);
  };

  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    onFieldChange(name, value);
  };

  // Hook para buscar especialidades usando useValueSet
  const { options: cboOptions, loading: cboLoading } = useValueSet({
    valueSet: "CBO",
    customUrl: "https://fluxmed.cloud/fhir/r4/ValueSet/CBO-S",
  });

  const { options: ufOptions, loading: ufLoading } = useValueSet({
    valueSet: "UF",
    customUrl:
      "https://fluxmed.com.br/hapi/fhir/ValueSet/1.3.6.1.4.1.54413.1.1.6.11",
  });

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Dados do profissional executante
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={5}>
          <TextField
            required
            fullWidth
            label="Nome do profissional"
            name="nomeProfissional"
            value={formData.nomeProfissional}
            onChange={handleChange}
            size="small"
            helperText="Nome do profissional executante"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small" required>
            <InputLabel>Conselho profissional</InputLabel>
            <Select
              name="conselhoProfissional"
              value={formData.conselhoProfissional}
              label="Conselho profissional"
              onChange={handleSelectChange}
            >
              {councilOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.display}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Conselho profissional</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField
            required
            fullWidth
            label="Número no conselho"
            name="numeroConselhoProfissional"
            value={formData.numeroConselhoProfissional}
            onChange={handleChange}
            size="small"
            helperText="Número no conselho"
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControl fullWidth size="small" required>
            <InputLabel>UF</InputLabel>
            <Select
              name="ufConselho"
              value={formData.ufConselho}
              label="UF"
              onChange={handleSelectChange}
            >
              {ufLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                ufOptions?.map((option) => (
                  <MenuItem key={option.code} value={option.value}>
                    {option.display}
                  </MenuItem>
                ))
              )}
            </Select>
            <FormHelperText>UF do conselho</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small" required>
            <InputLabel>Código CBO</InputLabel>
            <Select
              name="cbos"
              value={formData.cbos}
              label="Código CBO"
              onChange={handleSelectChange}
              disabled={cboLoading}
            >
              {cboLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                cboOptions?.map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {`${option.display} - ${option.code}`}
                  </MenuItem>
                ))
              )}
            </Select>
            <FormHelperText>
              Código brasileiro de ocupação em saúde
            </FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
    </Paper>
  );
};
